import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Button, CircularProgress, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { useConfirm } from 'material-ui-confirm';
import parse, { DOMNode } from 'html-react-parser';
import ContentEditor from 'components/ContentEditor';
import PolicyContentHelper from 'views/App/Portal/Policy/_helpers/policyContentHelper';
import useAccreditationStandardById from '../../../../_data_sources/AccreditationStandards/useStandard';
import { updateStandard, destroyStandard } from '../../../../_data_sources/AccreditationStandards/manageStandard';
import { translate as t } from 'hooks/useTranslations';
import type { AccreditationStandardNew } from '../../../../_types/awb';
import { useStandardsContext } from '../context';
import { useQueryClient } from 'react-query';
import TakeoverLayout from '_lex/_layouts/TakeoverLayout';
import { neutral } from '_lex/_foundations/phxColors';
import { ReactComponent as NoStandardSelected } from '../../images/no_standard_selected.svg';
import StandardsList from '../List';
import StandardActions from '../_components/StandardActions';
import EditorSheet from '_lex/_atoms/EditorSheet';
import { Delete } from '@mui/icons-material';
import errorHelper from 'helpers/errorHelper';
import { ServerErrorResponse } from 'data_sources/constants';
import { enqueueSnackbar } from 'notistack';

import ContentConversionHelper from 'views/App/Portal/Policy/DraftManuals/helpers/contentConversionHelper';
import WrittenDirectivesSummary from './WrittenDirectivesSummary';
import { Jurisdiction } from 'types/jurisdiction';

type StandardMode = 'EDITABLE' | 'EDITING';

const StandardDetail: React.FC = () => {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id: accreditationId, versionId, standardId } = useParams();
  const { getStandardType, isLoading: standardsLoading, version } = useStandardsContext();

  const [standardHeadings, setStandardHeadings] = useState<Record<string, string>>({});
  const [standardTitles, setStandardTitles] = useState<Record<string, string>>({});
  const [standardDraftContents, setStandardDraftContents] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [standardModes, setStandardModes] = useState<Record<string, StandardMode>>({});
  const [isDeleting, setIsDeleting] = useState(false);

  const standardByIdQuery = useAccreditationStandardById(accreditationId!, versionId!, standardId || '', undefined, {
    enabled: !isDeleting && !!standardId && !!accreditationId && !!versionId,
  });

  const isLoading = standardByIdQuery.isLoading || standardByIdQuery.isFetching || standardsLoading;
  const standardData = standardByIdQuery.data;

  const [standardType, setStandardType] = useState<'standard' | 'subStandard' | 'requirement' | undefined>(undefined);
  const [standardTypeText, setStandardTypeText] = useState<string>('');

  useEffect(() => {
    if (!standardId || !version || standardsLoading) {
      setStandardType('standard');
      setStandardTypeText(t('awb.standards.new.standard'));
      return;
    }

    const type = getStandardType(standardId);
    setStandardType(type);

    switch (type) {
      case 'standard':
        setStandardTypeText(t('awb.standards.new.standard'));
        break;
      case 'subStandard':
        setStandardTypeText(t('awb.standards.new.subStandard'));
        break;
      case 'requirement':
        setStandardTypeText(t('awb.standards.new.requirement'));
        break;
      default:
        setStandardTypeText(t('awb.standards.new.standard'));
    }
  }, [standardId, getStandardType, version, standardsLoading, t]);

  const getModeForStandard = (stdId: string): StandardMode => {
    return standardModes[stdId] ?? 'EDITABLE';
  };

  const setModeForStandard = (stdId: string, mode: StandardMode) => {
    setStandardModes((prev) => ({
      ...prev,
      [stdId]: mode,
    }));
  };

  const currentStandardMode = getModeForStandard(standardId || '');

  const getHeadingValue = (stdId: string) => standardHeadings[stdId] ?? '';
  const getTitleValue = (stdId: string) => standardTitles[stdId] ?? '';
  const getDraftContentValue = (stdId: string) => standardDraftContents[stdId] ?? '';

  const setHeadingValue = (stdId: string, value: string) => {
    setStandardHeadings((prev) => ({
      ...prev,
      [stdId]: value,
    }));
  };

  const setTitleValue = (stdId: string, value: string) => {
    setStandardTitles((prev) => ({
      ...prev,
      [stdId]: value,
    }));
  };

  const setDraftContentValue = (stdId: string, value: string) => {
    setStandardDraftContents((prev) => ({
      ...prev,
      [stdId]: value,
    }));
  };

  useEffect(() => {
    if (!isLoading && standardData) {
      setDraftContentValue(standardId || '', standardData.draftContent ?? '');
      setHeadingValue(standardId || '', standardData.heading ?? '');
      setTitleValue(standardId || '', standardData.title ?? '');
    }
  }, [standardId, standardData, isLoading]);

  const OnSaveContent = (draftContent: string) => {
    setDraftContentValue(standardId || '', draftContent);
  };

  const handleDiscard = () => {
    if (!standardData) {
      return;
    }

    setDraftContentValue(standardId || '', standardData.draftContent ?? '');
    setHeadingValue(standardId || '', standardData.heading ?? '');
    setTitleValue(standardId || '', standardData.title ?? '');
    setModeForStandard(standardId || '', 'EDITABLE');
  };

  const handleSaveStandard = async () => {
    const saveProcess = async () => {
      if (!standardData) {
        return;
      }

      setIsSaving(true);

      const headingValue = getHeadingValue(standardId || '');
      const titleValue = getTitleValue(standardId || '');
      const draftContentValue = getDraftContentValue(standardId || '');

      const standardPayload: AccreditationStandardNew = {
        ...standardData,
        position: Math.max(standardData.position ?? 1, 1),
        heading: headingValue,
        title: titleValue,
        draftContent: ContentConversionHelper.contentForRender(draftContentValue),
      } as AccreditationStandardNew;

      try {
        await updateStandard(accreditationId!, versionId!, standardId || '', standardPayload, async () => {
          await queryClient.invalidateQueries(['accreditation_standards', accreditationId, versionId]);
          await queryClient.invalidateQueries(['accreditation_standard', accreditationId, versionId, standardId || '']);

          if (standardData.parentStandardId) {
            await queryClient.invalidateQueries(['accreditation_standards', accreditationId, versionId]);
          }

          await standardByIdQuery.refetch();
          setModeForStandard(standardId || '', 'EDITABLE');
          enqueueSnackbar(t('awb.standards.detail.saveSuccess', '', { standardType: standardTypeText }), {
            variant: 'success',
            autoHideDuration: 6000,
          });
        });
      } catch (err) {
        let errorToPass: ServerErrorResponse;
        if (err instanceof Error && err.message) {
          try {
            errorToPass = JSON.parse(err.message);
          } catch {
            errorToPass = {
              message: err.message,
              errorInformationCode: undefined,
              detail: err.message,
              errorDetails: [],
            } as ServerErrorResponse;
          }
        } else {
          errorToPass = err as ServerErrorResponse;
        }
        errorHelper.enqueueErrors(errorToPass);
      } finally {
        setIsSaving(false);
      }
    };

    if (!standardData) {
      return;
    }

    if (standardData.publishedState === 'PUBLISHED') {
      confirm({
        title: 'Are you sure you want to proceed?',
        description: 'Changes could impact live accreditation customers.',
        cancellationButtonProps: {
          variant: 'outlined',
          color: 'neutral',
        },
        confirmationButtonProps: {
          variant: 'contained',
          color: 'error',
        },
        confirmationText: 'Update',
        cancellationText: 'Cancel',
      })
        .then(saveProcess)
        .catch(() => {
          // User cancelled the operation
        });
    } else {
      await saveProcess();
    }
  };

  const handleDelete = async () => {
    confirm({
      title: t('awb.standards.detail.deleteConfirm.title', '', { standardType: standardTypeText }),
      description: t('awb.standards.detail.deleteConfirm.description', '', { standardType: standardTypeText }),
      cancellationButtonProps: {
        variant: 'outlined',
        color: 'neutral',
      },
      confirmationButtonProps: {
        variant: 'contained',
        color: 'error',
      },
      confirmationText: t('awb.standards.detail.deleteConfirm.confirm'),
      cancellationText: t('awb.standards.detail.deleteConfirm.cancel'),
    })
      .then(async () => {
        if (!standardData) {
          return;
        }

        setIsDeleting(true);

        await destroyStandard(
          accreditationId!,
          versionId!,
          standardId!,
          async () => {
            await queryClient.invalidateQueries(['accreditation_standards', accreditationId, versionId]);
            if (standardData.parentStandardId) {
              await queryClient.invalidateQueries([
                'accreditation_standards',
                accreditationId,
                versionId,
                standardData.parentStandardId,
              ]);
            }
            await queryClient.refetchQueries(['accreditation_standards', accreditationId, versionId]);

            if (standardData.parentStandardId) {
              navigate(
                `/admin/awb/accrediting-bodies/${accreditationId}/versions/${versionId}/standards/${standardData.parentStandardId}`
              );
            } else {
              navigate(`/admin/awb/accrediting-bodies/${accreditationId}/versions/${versionId}/standards`);
            }

            enqueueSnackbar(t('awb.standards.detail.deleteSuccess', '', { standardType: standardTypeText }), {
              variant: 'success',
            });
          },
          (err) => {
            errorHelper.enqueueErrors(err as ServerErrorResponse);
            setIsDeleting(false);
          }
        );
      })
      .catch(() => {
        // User cancelled the deletion
      });
  };

  const renderContent = () => {
    if (!standardId) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            minHeight: '60vh',
          }}
        >
          <NoStandardSelected style={{ height: '240px' }} />
          <Typography sx={{ color: neutral[500] }}>{t('awb.standards.emptyScreen')}</Typography>
        </Box>
      );
    }

    const headingValue = getHeadingValue(standardId);
    const titleValue = getTitleValue(standardId);
    const draftContentValue = getDraftContentValue(standardId);

    return (
      <EditorSheet>
        {isLoading ? (
          <CircularProgress />
        ) : (
          <Box>
            {currentStandardMode === 'EDITING' ? (
              <Stack spacing={3}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant='eyebrowMd'>{standardTypeText}</Typography>
                  {standardData?.publishedState !== 'PUBLISHED' && (
                    <Tooltip
                      title={
                        standardData && standardData.standardCount > 0
                          ? 'This standard cannot be deleted while it has substandards'
                          : t('awb.standards.detail.delete', '', { standardType: standardTypeText })
                      }
                    >
                      <span>
                        <Button
                          variant='outlined'
                          color='neutral'
                          size='small'
                          className='xs icon-only'
                          onClick={handleDelete}
                          disabled={standardData && standardData.standardCount > 0}
                        >
                          <Delete />
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                </Box>
                <Box sx={{ display: 'flex', width: '100%', gap: 2, mb: 4 }}>
                  <TextField
                    placeholder='Enter #'
                    hiddenLabel
                    value={headingValue}
                    onChange={(e) => setHeadingValue(standardId || '', e.target.value)}
                    sx={{ width: '15%', pt: 0 }}
                  />
                  <TextField
                    placeholder='Enter Standard Title'
                    hiddenLabel
                    value={titleValue}
                    onChange={(e) => setTitleValue(standardId || '', e.target.value)}
                    sx={{ width: '85%', pt: 0 }}
                  />
                </Box>
                <Stack spacing={3}>
                  <ContentEditor
                    id={`content_${standardId}`}
                    key={`content_${standardId}`}
                    contentHTML={draftContentValue}
                    onSave={OnSaveContent}
                    isEditable
                    trackChangesEnabled={false}
                    textEntitiesEnabled={false}
                    crossReferencesEnabled={false}
                    imagesEnabled={false}
                    attachmentsEnabled={false}
                  />
                </Stack>
              </Stack>
            ) : (
              <Stack spacing={3}>
                <Typography variant='eyebrowMd'>{standardTypeText}</Typography>
                <Typography variant='subtitleMd'>{`${headingValue} ${titleValue}`}</Typography>
                <Box sx={{ py: 3, minHeight: 0, overflow: 'auto' }}>
                  {parse(draftContentValue ?? '', {
                    replace(node: DOMNode, ind: number) {
                      return PolicyContentHelper.handleContent(node, ind);
                    },
                  })}
                </Box>
              </Stack>
            )}
          </Box>
        )}
      </EditorSheet>
    );
  };

  return (
    <TakeoverLayout
      pageTitle={version?.version || ''}
      backUrl={`/admin/awb/accrediting-bodies/${accreditationId}/versions/`}
      menubarLeft={<StandardActions />}
      leftDrawer={<StandardsList />}
      contentBackground='neutral'
      rightDrawer={
        standardId &&
        version && (
          <WrittenDirectivesSummary
            standardId={standardId}
            jurisdictions={
              Array.isArray(version?.jurisdictions) && typeof version?.jurisdictions[0] === 'object'
                ? (version.jurisdictions as Jurisdiction[])
                : []
            }
          />
        )
      }
      contentHeaderRight={
        standardId &&
        (currentStandardMode === 'EDITING' ? (
          <Button
            variant='contained'
            color='primary'
            size='small'
            onClick={handleSaveStandard}
            disabled={isSaving}
            endIcon={
              isSaving ? (
                <CircularProgress
                  size={16}
                  color='inherit'
                />
              ) : undefined
            }
          >
            {t('awb.standards.detail.save')}
          </Button>
        ) : (
          <Button
            variant='contained'
            color='primary'
            size='small'
            onClick={() => setModeForStandard(standardId, 'EDITING')}
          >
            {t('awb.standards.detail.edit')}
          </Button>
        ))
      }
    >
      {renderContent()}
    </TakeoverLayout>
  );
};

export default StandardDetail;
